import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { apiConfig } from '../config/api'
import { invalidateCachedImage } from '../hooks/useCachedImage'
import { auth } from '../config/firebase'
import { compressImage, needsCompression } from '../utils/imageCompression'

// Keep emulator detection consistent with firebase.ts (only treat as emulator in dev)
const isAuthEmulator = Boolean(import.meta.env.DEV && import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST)

const pickNonEmptyString = (...candidates: Array<unknown>): string => {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 0) return c
  }
  return ''
}

const pickArray = <T,>(candidate: unknown, fallback: T[]): T[] => {
  return Array.isArray(candidate) ? (candidate as T[]) : fallback
}

interface BasicInfo {
  name: string
  who_you_help?: string
  headline?: string
  offer_primary_outcome?: string
  offer_timeframe?: string
  offer_delivery?: string
  offer_exclusions?: string[]
  carousel_bg_palette?: string
  photo_url?: string
  photo_status?: 'pending' | 'committed'
  photo_storage_path?: string
  photo_cutout_url?: string
  photo_focal_x?: number
  photo_focal_y?: number
  photo_scale?: number
  photo_fit_mode?: 'cover' | 'contain'
  photo_gradient_side?: 'left' | 'right'
  photo_reveal?: number
  cta_type?: 'book_call'
  specialty?: string
  location?: { city?: string; country?: string }
  client_types?: string[]
  tagline?: string
  website_url?: string
  booking_url?: string
  logo_url?: string
}

interface DetailedInterview {
  ideal_client?: string
  problems_solved?: string
  transformation?: string
  approach?: string
  before_moment?: string
  turning_point?: string
  approach_one_liner?: string
  first_call_expectations?: string
  exclusions?: string[]
  tools_methods?: string[]
  certifications?: string[]
  offers?: string[]
  packages?: string[]
  tone_preferences?: string[]
  testimonials?: string[]
  credentials?: string[]
  specialties?: string[]
  social_links?: Array<{ platform: string; url: string }>
}

interface VisibilityScore {
  current: number
  breakdown: {
    completeness: number
    quality: number
  }
  recommendations: Array<{
    action: string
    potential_increase: number
    category: string
  }>
}

type LandingPageCtaType = 'book_call' | 'ask_question'
type LandingPageCtaMode = 'calendar_only' | 'lead_form_then_schedule'

interface LandingPageDraft {
  slug: string
  template_id: 'spotlight_v1'
  template_version: number
  practitioner: {
    name: string
    specialty: string
    photo_url?: string
    logo_url?: string
  }
  cta_config: {
    cta_type: LandingPageCtaType
    cta_mode: LandingPageCtaMode
    booking_url?: string
    form_fields: string[]
    required_fields?: string[]
  }
  sections: {
    hero: { headline: string; subheadline: string }
    offer: { headline: string; bullets: string[] }
    trust: { headline: string; bullets: string[] }
    about: { headline: string; body: string }
    faq: { headline: string; items: Array<{ q: string; a: string }> }
    cta: { headline: string; button_text: string; microcopy?: string }
  }
  links?: {
    website_url?: string
    social_links?: Array<{ platform: string; url: string }>
  }
  updated_at?: any
}

interface ConversionReadinessStatus {
  is_ready: boolean
  last_validated_at?: any
  checks: Record<
    string,
    {
      ok: boolean
      message?: string
    }
  >
}

interface ConversionReadinessConfig {
  notification_emails: string[]
  email_autoresponder: {
    enabled: boolean
    from_name: string
    from_email: string
    subject: string
    body: string
  }
  followup_sequence: {
    enabled: boolean
    template_id: string
    steps: Array<{
      delay_minutes: number
      channel: 'email'
      subject: string
      body: string
    }>
  }
  availability: {
    timezone: string
    weekly_windows: Array<{ day: 0 | 1 | 2 | 3 | 4 | 5 | 6; start: string; end: string }>
  }
  status?: ConversionReadinessStatus
}

export interface GoogleReview {
  author_name: string
  rating: number
  text: string
  relative_time: string
  profile_photo_url?: string
}

export interface ReviewsConfig {
  google?: {
    place_id: string
    business_name: string
    connected_at: string
    last_synced_at: string
  }
  cached_reviews?: GoogleReview[]
  aggregate_rating?: number
  total_reviews?: number
  google_maps_url?: string
  display_settings?: {
    max_reviews?: number
    min_rating?: number
  }
}

interface CarouselCandidate {
  template_id: string
  template_name: string
  primary_message: string
  slides: Array<{
    type: 'identity' | 'before' | 'after' | 'cta' | 'proof_or_filter'
    headline: string
    body: string
    image_url?: string
    cta_text?: string
    cta_url?: string
  }>
}

interface GeneratedAssets {
  ad_candidates?: Array<{ headline: string; body: string; rationale?: string }>
  carousel_candidates?: Array<CarouselCandidate>
  original_carousel_candidates?: Array<CarouselCandidate>  // Unpolished versions for "See original"
  copy_polished?: boolean  // Whether AI polishing was applied
  selected_ad?: { headline: string; body: string }
  landing_page?: LandingPageDraft
}

export type OnboardingMode = 'service' | 'product'

interface OnboardingState {
  onboardingMode: OnboardingMode
  step: number
  basic_info: BasicInfo
  detailed_interview: DetailedInterview
  visibility_score: VisibilityScore
  generated_assets: GeneratedAssets
  conversion_readiness: ConversionReadinessConfig
  reviews_config: ReviewsConfig
  isLoading: boolean
  isSaving: boolean
  error: string | null
}

interface OnboardingContextType extends OnboardingState {
  setOnboardingMode: (mode: OnboardingMode) => void
  updateBasicInfo: (info: Partial<BasicInfo>) => void
  updateDetailedInterview: (interview: Partial<DetailedInterview>) => void
  updateReviewsConfig: (config: ReviewsConfig) => void
  saveBasicSetup: () => Promise<void>
  generatePreview: () => Promise<void>
  saveDetailedInterviewBlock: (block: Partial<DetailedInterview>) => Promise<void>
  generateAssets: () => Promise<void>
  saveAssets: (selectedAd?: { headline: string; body: string }, ctaType?: string) => Promise<void>
  loadLandingPageDraft: () => Promise<void>
  saveLandingPageDraft: (landingPage: LandingPageDraft, conversionReadiness?: ConversionReadinessConfig) => Promise<void>
  validateConversionReadiness: () => Promise<ConversionReadinessStatus>
  sendConversionReadinessTest: (kind: 'lead_notification' | 'autoresponder') => Promise<void>
  loadProgress: (currentUser?: { displayName?: string | null; email?: string | null } | null) => Promise<void>
  loadReviewsConfig: () => Promise<void>
  getAuthToken: () => string | null
  resetState: () => void
  uploadPhoto: (file: File, onProgress?: (progress: number) => void) => Promise<{
    photo_url: string
    storage_path: string
    status: 'pending' | 'committed'
    photo_cutout_url?: string
  }>
}

export const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined)

const initialState: OnboardingState = {
  onboardingMode: 'service',
  step: 0,
  basic_info: {
    name: '',
    who_you_help: '',
    headline: '',
    client_types: [],
    tagline: '',
    cta_type: 'book_call',
    offer_primary_outcome: '',
    offer_timeframe: '',
    offer_delivery: '',
    offer_exclusions: [],
    carousel_bg_palette: 'brand',
    photo_focal_x: 50,
    photo_focal_y: 50,
    photo_scale: 1,
    photo_fit_mode: 'cover',
    photo_gradient_side: 'left',
    photo_reveal: 0.22
  },
  detailed_interview: {},
  visibility_score: {
    current: 0,
    breakdown: { completeness: 0, quality: 0 },
    recommendations: []
  },
  generated_assets: {},
  conversion_readiness: {
    notification_emails: [],
    email_autoresponder: { enabled: false, from_name: '', from_email: '', subject: '', body: '' },
    followup_sequence: { enabled: false, template_id: '', steps: [] },
    availability: { timezone: '', weekly_windows: [] },
    status: { is_ready: false, checks: {} }
  },
  reviews_config: {},
  isLoading: false,
  isSaving: false,
  error: null
}

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<OnboardingState>(initialState)
  const loadingRef = useRef(false) // Track if loadProgress is currently running
  const stateRef = useRef(state) // Track current state for callbacks
  useEffect(() => {
    stateRef.current = state
  }, [state])

  const setOnboardingMode = useCallback((mode: OnboardingMode) => {
    setState(prev => ({ ...prev, onboardingMode: mode }))
  }, [])

  const getAuthToken = useCallback(() => {
    return localStorage.getItem('firebase_id_token')
  }, [])

  const resetState = useCallback(() => {
    setState(initialState)
    // Clear onboarding-related localStorage
    localStorage.removeItem('onboarding_basic_info')
    loadingRef.current = false
  }, [])

  const loadProgress = useCallback(async (currentUser?: { displayName?: string | null; email?: string | null } | null) => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      // No token, don't try to load
      return
    }

    // Prevent multiple simultaneous calls using ref
    if (loadingRef.current) {
      return // Already loading, skip this call
    }

    loadingRef.current = true
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/progress`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.status === 401) {
        // Unauthorized - token expired or invalid
        // Clear token and don't retry
        localStorage.removeItem('firebase_id_token')
        setState(prev => ({
          ...prev,
          error: null, // Don't show error for auth failures
          isLoading: false
        }))
        loadingRef.current = false
        return
      }

      if (!response.ok) {
        throw new Error('Failed to load progress')
      }

      const data = await response.json()
      setState(prev => {
        const backendBasicInfo = data.basic_info || {}
        // Clear any previous errors when we successfully load progress
        // This prevents stale errors from persisting across page navigations
        
        // Determine name with correct priority:
        // 1. FIRST: Use name from basic_info (what user explicitly provided in onboarding)
        // 2. SECOND: Fall back to Firebase auth displayName (only if basic_info.name is empty)
        // 3. THIRD: Extract from email as last resort
        // This ensures the professional/business name they entered takes precedence over personal auth name
        let resolvedName: string
        const storedName = pickNonEmptyString(backendBasicInfo.name, prev.basic_info.name)

        if (storedName) {
          // Use the name they explicitly provided in basic setup (professional/business name)
          resolvedName = storedName
        } else {
          // Only if no stored name, fall back to auth name
          const user = currentUser || auth.currentUser
          const firebaseDisplayName = user?.displayName || ''
          const userEmail = user?.email || localStorage.getItem('user_email') || ''

          if (firebaseDisplayName && firebaseDisplayName.trim()) {
            // Use Firebase auth displayName
            resolvedName = firebaseDisplayName
          } else if (userEmail) {
            // Extract name from email as last resort (e.g., "john.doe@email.com" -> "John Doe")
            resolvedName = userEmail.split('@')[0].split(/[._-]/).map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(' ')
          } else {
            resolvedName = ''
          }
        }
        
        const mappedBasicInfo: BasicInfo = {
          ...prev.basic_info,
          ...backendBasicInfo,
          // Use resolved name that prioritizes current auth user
          name: resolvedName,
          who_you_help: pickNonEmptyString(
            backendBasicInfo.who_you_help,
            backendBasicInfo.client_types?.[0],
            prev.basic_info.who_you_help
          ),
          headline: pickNonEmptyString(
            backendBasicInfo.headline,
            backendBasicInfo.tagline,
            prev.basic_info.headline
          ),
          tagline: pickNonEmptyString(
            backendBasicInfo.tagline,
            backendBasicInfo.headline,
            prev.basic_info.tagline
          ),
          offer_primary_outcome: pickNonEmptyString(
            backendBasicInfo.offer_primary_outcome,
            prev.basic_info.offer_primary_outcome
          ),
          offer_timeframe: pickNonEmptyString(
            backendBasicInfo.offer_timeframe,
            prev.basic_info.offer_timeframe
          ),
          offer_delivery: pickNonEmptyString(
            backendBasicInfo.offer_delivery,
            prev.basic_info.offer_delivery
          ),
          carousel_bg_palette: pickNonEmptyString(
            backendBasicInfo.carousel_bg_palette,
            prev.basic_info.carousel_bg_palette,
            'brand'
          ),
          photo_url: pickNonEmptyString(backendBasicInfo.photo_url, prev.basic_info.photo_url),
          photo_focal_x: typeof backendBasicInfo.photo_focal_x === 'number' ? backendBasicInfo.photo_focal_x : prev.basic_info.photo_focal_x,
          photo_focal_y: typeof backendBasicInfo.photo_focal_y === 'number' ? backendBasicInfo.photo_focal_y : prev.basic_info.photo_focal_y,
          photo_scale: typeof backendBasicInfo.photo_scale === 'number' ? backendBasicInfo.photo_scale : prev.basic_info.photo_scale,
          photo_fit_mode: (backendBasicInfo.photo_fit_mode || prev.basic_info.photo_fit_mode || 'cover') as BasicInfo['photo_fit_mode'],
          photo_gradient_side: (backendBasicInfo.photo_gradient_side || prev.basic_info.photo_gradient_side || 'left') as BasicInfo['photo_gradient_side'],
          photo_reveal: typeof backendBasicInfo.photo_reveal === 'number' ? backendBasicInfo.photo_reveal : prev.basic_info.photo_reveal,
          booking_url: pickNonEmptyString(backendBasicInfo.booking_url, prev.basic_info.booking_url),
          website_url: pickNonEmptyString(backendBasicInfo.website_url, prev.basic_info.website_url),
          logo_url: pickNonEmptyString(backendBasicInfo.logo_url, prev.basic_info.logo_url),
          client_types: pickArray<string>(backendBasicInfo.client_types, prev.basic_info.client_types ?? []),
          offer_exclusions: pickArray<string>(backendBasicInfo.offer_exclusions, prev.basic_info.offer_exclusions ?? []),
          cta_type: (backendBasicInfo.cta_type || prev.basic_info.cta_type || 'book_call') as BasicInfo['cta_type']
        }

        return {
          ...prev,
          step: data.step || 0,
          basic_info: mappedBasicInfo,
          detailed_interview: data.detailed_interview || prev.detailed_interview,
          visibility_score: data.visibility_score || prev.visibility_score,
          generated_assets: data.generated_assets || prev.generated_assets,
          conversion_readiness: data.conversion_readiness || prev.conversion_readiness,
          reviews_config: data.reviews_config || prev.reviews_config,
          isLoading: false,
          error: null  // Clear any previous errors on successful load
        }
      })
      loadingRef.current = false
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Failed to load progress',
        isLoading: false
      }))
      loadingRef.current = false
    }
  }, [])

  const updateBasicInfo = useCallback((info: Partial<BasicInfo>) => {
    setState(prev => ({
      ...prev,
      basic_info: { ...prev.basic_info, ...info }
    }))
  }, [])

  const updateDetailedInterview = useCallback((interview: Partial<DetailedInterview>) => {
    setState(prev => ({
      ...prev,
      detailed_interview: { ...prev.detailed_interview, ...interview }
    }))
  }, [])

  const updateReviewsConfig = useCallback((config: ReviewsConfig) => {
    setState(prev => ({
      ...prev,
      reviews_config: config
    }))
  }, [])

  const loadReviewsConfig = useCallback(async () => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) return

    try {
      const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/reviews/google`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.connected) {
          setState(prev => ({
            ...prev,
            reviews_config: {
              google: {
                place_id: data.place_id,
                business_name: data.business_name,
                connected_at: '',
                last_synced_at: data.last_synced_at || ''
              },
              cached_reviews: data.reviews || [],
              aggregate_rating: data.aggregate_rating,
              total_reviews: data.total_reviews,
              google_maps_url: data.google_maps_url
            }
          }))
        }
      }
    } catch (error) {
      // Silently fail - reviews are optional
      console.warn('Failed to load reviews config:', error)
    }
  }, [])

  const saveBasicSetup = useCallback(async () => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      throw new Error('Authentication required')
    }

    setState(prev => ({ ...prev, isSaving: true, error: null }))

    try {
      // Use stateRef.current to avoid recreating this callback on every basic_info change
      const basicInfo = stateRef.current.basic_info
      const payloadBasicInfo: BasicInfo = {
        name: basicInfo.name,
        specialty: basicInfo.specialty || '',
        location: basicInfo.location || {},  // Ensure location is always an object
        client_types: basicInfo.who_you_help
          ? [basicInfo.who_you_help]
          : basicInfo.client_types || [],
        tagline: basicInfo.headline || basicInfo.tagline || '',
        offer_primary_outcome: basicInfo.offer_primary_outcome || '',
        offer_timeframe: basicInfo.offer_timeframe || '',
        offer_delivery: basicInfo.offer_delivery || '',
        offer_exclusions: basicInfo.offer_exclusions || [],
        carousel_bg_palette: basicInfo.carousel_bg_palette || 'brand',
        photo_url: basicInfo.photo_url || '',
        photo_status: basicInfo.photo_status || '',
        photo_storage_path: basicInfo.photo_storage_path || '',
        photo_focal_x: typeof basicInfo.photo_focal_x === 'number' ? basicInfo.photo_focal_x : 50,
        photo_focal_y: typeof basicInfo.photo_focal_y === 'number' ? basicInfo.photo_focal_y : 50,
        photo_scale: typeof basicInfo.photo_scale === 'number' ? basicInfo.photo_scale : 1,
        photo_fit_mode: (basicInfo.photo_fit_mode || 'cover') as BasicInfo['photo_fit_mode'],
        photo_gradient_side: (basicInfo.photo_gradient_side || 'left') as BasicInfo['photo_gradient_side'],
        photo_reveal: typeof basicInfo.photo_reveal === 'number' ? basicInfo.photo_reveal : 0.22,
        website_url: basicInfo.website_url || '',
        booking_url: basicInfo.booking_url || '',
        logo_url: basicInfo.logo_url || ''
      }

      // Create an AbortController for timeout handling
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout

      let response: Response
      try {
        response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/basic-setup`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            basic_info: payloadBasicInfo
          }),
          signal: controller.signal
        })
      } catch (fetchError: any) {
        clearTimeout(timeoutId)
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timed out. Please check your connection and try again.')
        }
        if (fetchError.message?.includes('Failed to fetch') || fetchError.message?.includes('ERR_CONNECTION')) {
          throw new Error(`Cannot connect to server at ${apiConfig.baseURL}. Please ensure the backend is running.`)
        }
        throw new Error(`Network error: ${fetchError.message || 'Unknown error'}`)
      } finally {
        clearTimeout(timeoutId)
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || 'Failed to save basic setup')
      }

      const data = await response.json()

      // Only update step and visibility score, NOT the basic_info
      // This preserves any text the user has typed since the save started
      setState(prev => ({
        ...prev,
        step: data.step || 1,
        visibility_score: data.visibility_score || prev.visibility_score,
        isSaving: false
      }))

      // Save current state to localStorage as backup
      localStorage.setItem('onboarding_basic_info', JSON.stringify(stateRef.current.basic_info))

    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Failed to save basic setup',
        isSaving: false
      }))
      throw error
    }
  }, [])

  const generatePreview = useCallback(async () => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      throw new Error('Authentication required')
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/generate-preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to generate preview')
      }

      const data = await response.json()
      setState(prev => ({
        ...prev,
        step: 2,
        generated_assets: {
          ...prev.generated_assets,
          ad_candidates: data.ad_candidates,
          carousel_candidates: data.carousel_candidates,
          original_carousel_candidates: data.original_carousel_candidates,
          copy_polished: data.copy_polished
        },
        visibility_score: data.visibility_score || prev.visibility_score,
        isLoading: false
      }))
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Failed to generate preview',
        isLoading: false
      }))
      throw error
    }
  }, [])

  const saveDetailedInterviewBlock = useCallback(async (block: Partial<DetailedInterview>) => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      throw new Error('Authentication required')
    }

    setState(prev => ({ ...prev, isSaving: true, error: null }))

    try {
      // Find block number based on what fields are in the block
      let blockNumber = 1
      if (block.ideal_client || block.problems_solved || block.transformation) {
        blockNumber = 1
      } else if (block.approach || block.tools_methods || block.certifications) {
        blockNumber = 2
      } else if (block.offers || block.packages) {
        blockNumber = 3
      } else if (block.tone_preferences) {
        blockNumber = 4
      } else {
        blockNumber = 5
      }

      const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/detailed-interview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          block: {
            block_number: blockNumber,
            ...block
          }
        })
      })

      if (!response.ok) {
        throw new Error('Failed to save detailed interview')
      }

      const data = await response.json()
      setState(prev => ({
        ...prev,
        step: 4,
        detailed_interview: { ...prev.detailed_interview, ...block },
        generated_assets: {
          ...prev.generated_assets,
          ad_candidates: data.ad_candidates || prev.generated_assets.ad_candidates,
          carousel_candidates: data.carousel_candidates || prev.generated_assets.carousel_candidates,
          original_carousel_candidates: data.original_carousel_candidates || prev.generated_assets.original_carousel_candidates,
          copy_polished: data.copy_polished ?? prev.generated_assets.copy_polished
        },
        visibility_score: data.visibility_score || prev.visibility_score,
        isSaving: false
      }))
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Failed to save detailed interview',
        isSaving: false
      }))
      throw error
    }
  }, [])

  const uploadToSignedUrl = useCallback((file: File, uploadUrl: string, signedContentType: string, onProgress?: (progress: number) => void) => {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100
          onProgress(progress)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed due to network error'))
      })
      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload timed out'))
      })

      xhr.open('PUT', uploadUrl)
      // Use the exact content_type from backend - must match what was signed exactly
      const contentType = (signedContentType || file.type || 'application/octet-stream').trim()
      xhr.setRequestHeader('Content-Type', contentType)
      xhr.timeout = 300000
      xhr.send(file)
    })
  }, [])

  const uploadPhoto = useCallback(async (file: File, onProgress?: (progress: number) => void) => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      throw new Error('Authentication required')
    }

    // Invalidate cached blob URL for the old photo (if any) so the new one loads fresh
    if (stateRef.current.basic_info.photo_url) {
      invalidateCachedImage(stateRef.current.basic_info.photo_url)
    }
    if (stateRef.current.basic_info.photo_cutout_url) {
      invalidateCachedImage(stateRef.current.basic_info.photo_cutout_url)
    }

    const normalizedType = (file.type || '').toLowerCase()
    const fileName = file.name.toLowerCase()
    
    // Check for HEIC/HEIF files (iPhone default format)
    const isHeic = normalizedType.includes('heic') || 
                   normalizedType.includes('heif') ||
                   fileName.endsWith('.heic') ||
                   fileName.endsWith('.heif')
    
    if (isHeic) {
      throw new Error('HEIC format is not supported. Please convert your photo to JPEG. On iPhone: Settings > Camera > Formats > Most Compatible, or select "Automatic" when uploading.')
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg']
    // Allow files with empty type if they look like images (iOS Safari sometimes does this)
    if (normalizedType && !allowedTypes.includes(normalizedType) && !normalizedType.startsWith('image/')) {
      throw new Error('Only JPG or PNG images are allowed')
    }

    // Compress image if it's too large (client-side compression for better UX)
    let fileToUpload = file
    if (needsCompression(file)) {
      try {
        fileToUpload = await compressImage(file)
      } catch (compressionError) {
        // If compression fails, still try to upload original (server will validate)
        console.warn('Image compression failed, proceeding with original file:', compressionError)
      }
    }

    // Final validation check (safety net - server will also validate)
    if (fileToUpload.size > 5 * 1024 * 1024) {
      throw new Error('File size must be 5MB or smaller. Please try a smaller image.')
    }

    // Create an AbortController for timeout handling
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout

    let uploadUrlResponse: Response
    try {
      uploadUrlResponse = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/photo-upload-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file_name: fileToUpload.name,
          file_type: fileToUpload.type || 'image/jpeg', // Compressed images are JPEG
          file_size: fileToUpload.size
        }),
        signal: controller.signal
      })
    } catch (fetchError: any) {
      clearTimeout(timeoutId)
      if (fetchError.name === 'AbortError') {
        throw new Error('Request timed out. Please check your connection and try again.')
      }
      if (fetchError.message?.includes('Failed to fetch') || fetchError.message?.includes('ERR_CONNECTION')) {
        throw new Error(`Cannot connect to server at ${apiConfig.baseURL}. Please ensure the backend is running.`)
      }
      throw new Error(`Network error: ${fetchError.message || 'Unknown error'}`)
    } finally {
      clearTimeout(timeoutId)
    }

    if (!uploadUrlResponse.ok) {
      const errorData = await uploadUrlResponse.json().catch(() => ({}))
      throw new Error(errorData.detail || 'Failed to create upload URL')
    }

    const uploadData = await uploadUrlResponse.json()
    await uploadToSignedUrl(fileToUpload, uploadData.upload_url, uploadData.content_type, onProgress)

    // Automatically commit photo after upload to trigger background removal
    let resolvedPhotoUrl = (uploadData.photo_url || uploadData.public_url || '').toString()
    let photoStatus = 'pending'
    let photoCutoutUrl: string | undefined = undefined

    try {
      const commitResponse = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/photo-commit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          storage_path: uploadData.storage_path
        })
      })

      if (commitResponse.ok) {
        const commitData = await commitResponse.json()
        resolvedPhotoUrl = commitData.photo_url || resolvedPhotoUrl
        photoStatus = 'committed'
        photoCutoutUrl = commitData.photo_cutout_url
      } else {
        // If commit fails, continue with pending status (background removal will be skipped)
        console.warn('Photo commit failed, using pending status:', await commitResponse.text())
      }
    } catch (commitError) {
      // If commit fails, continue with pending status (background removal will be skipped)
      console.warn('Photo commit error, using pending status:', commitError)
    }

    updateBasicInfo({
      photo_url: resolvedPhotoUrl,
      photo_status: photoStatus,
      photo_storage_path: uploadData.storage_path,
      ...(photoCutoutUrl && { photo_cutout_url: photoCutoutUrl })
    })

    return {
      photo_url: resolvedPhotoUrl,
      storage_path: uploadData.storage_path as string,
      status: photoStatus as 'pending' | 'committed',
      photo_cutout_url: photoCutoutUrl
    }
  }, [updateBasicInfo, uploadToSignedUrl])

  const generateAssets = useCallback(async () => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      throw new Error('Authentication required')
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/generate-assets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to generate assets')
      }

      const data = await response.json()
      setState(prev => ({
        ...prev,
        step: 5,
        generated_assets: {
          ...prev.generated_assets,
          ad_candidates: data.ads,
          landing_page: data.landing_page
        },
        conversion_readiness: data.conversion_readiness || prev.conversion_readiness,
        visibility_score: data.visibility_score || prev.visibility_score,
        isLoading: false
      }))
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Failed to generate assets',
        isLoading: false
      }))
      throw error
    }
  }, [])

  const loadLandingPageDraft = useCallback(async () => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) throw new Error('Authentication required')

    setState(prev => ({ ...prev, isLoading: true, error: null }))
    try {
      const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/landing-page-draft`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      if (!response.ok) {
        throw new Error('Failed to load landing page draft')
      }
      const data = await response.json()
      setState(prev => ({
        ...prev,
        generated_assets: {
          ...prev.generated_assets,
          landing_page: data.landing_page
        },
        conversion_readiness: data.conversion_readiness || prev.conversion_readiness,
        isLoading: false
      }))
    } catch (error: any) {
      setState(prev => ({ ...prev, error: error.message || 'Failed to load landing page draft', isLoading: false }))
      throw error
    }
  }, [])

  const saveLandingPageDraft = useCallback(async (landingPage: LandingPageDraft, conversionReadiness?: ConversionReadinessConfig) => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) throw new Error('Authentication required')

    setState(prev => ({ ...prev, isSaving: true, error: null }))
    try {
      // Get current state to preserve required fields
      const existingLandingPage = stateRef.current.generated_assets.landing_page

      // Preserve required fields from existing landing page if available
      const landingPageToSave: LandingPageDraft = {
        ...existingLandingPage,
        ...landingPage,
        // Ensure required fields are present
        slug: landingPage.slug || existingLandingPage?.slug || '',
        template_id: landingPage.template_id || existingLandingPage?.template_id || 'spotlight_v1',
        template_version: landingPage.template_version || existingLandingPage?.template_version || 1,
        // Ensure nested objects are properly structured
        practitioner: {
          ...(existingLandingPage?.practitioner || {}),
          ...(landingPage.practitioner || {})
        },
        cta_config: {
          ...(existingLandingPage?.cta_config || {}),
          ...(landingPage.cta_config || {}),
          form_fields: landingPage.cta_config?.form_fields || existingLandingPage?.cta_config?.form_fields || ['name', 'email', 'phone', 'message'],
          required_fields: landingPage.cta_config?.required_fields || existingLandingPage?.cta_config?.required_fields || ['name', 'email', 'phone']
        },
        sections: {
          ...(existingLandingPage?.sections || {}),
          ...(landingPage.sections || {})
        },
        links: {
          ...(existingLandingPage?.links || {}),
          ...(landingPage.links || {})
        }
      }

      const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/landing-page-draft`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          landing_page: landingPageToSave,
          ...(conversionReadiness ? { conversion_readiness: conversionReadiness } : {})
        })
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData) || 'Failed to save landing page draft'
        throw new Error(errorMessage)
      }
      setState(prev => ({
        ...prev,
        generated_assets: { ...prev.generated_assets, landing_page: landingPageToSave },
        ...(conversionReadiness ? { conversion_readiness: conversionReadiness } : {}),
        isSaving: false
      }))
    } catch (error: any) {
      setState(prev => ({ ...prev, error: error.message || 'Failed to save landing page draft', isSaving: false }))
      throw error
    }
  }, [])

  const validateConversionReadiness = useCallback(async (): Promise<ConversionReadinessStatus> => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) throw new Error('Authentication required')

    const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/conversion-readiness/validate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    if (!response.ok) {
      throw new Error('Failed to validate readiness')
    }
    const data = await response.json()
    const status = data.status as ConversionReadinessStatus
    setState(prev => ({
      ...prev,
      conversion_readiness: { ...prev.conversion_readiness, status }
    }))
    return status
  }, [])

  const sendConversionReadinessTest = useCallback(async (kind: 'lead_notification' | 'autoresponder') => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) throw new Error('Authentication required')

    const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/conversion-readiness/test`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ kind })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || 'Failed to send test email')
    }
  }, [])

  const saveAssets = useCallback(async (
    selectedAd?: { headline: string; body: string },
    ctaType?: string
  ) => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      throw new Error('Authentication required')
    }

    setState(prev => ({ ...prev, isSaving: true, error: null }))

    try {
      const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/save-assets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          selected_ad: selectedAd,
          landing_page_cta_type: ctaType
        })
      })

      if (!response.ok) {
        throw new Error('Failed to save assets')
      }

      if (selectedAd) {
        setState(prev => ({
          ...prev,
          generated_assets: {
            ...prev.generated_assets,
            selected_ad: selectedAd
          },
          isSaving: false
        }))
      } else {
        setState(prev => ({ ...prev, isSaving: false }))
      }
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Failed to save assets',
        isSaving: false
      }))
      throw error
    }
  }, [])

  // Load progress on mount if authenticated
  // Only run once on mount, not on every token change
  useEffect(() => {
    const token = localStorage.getItem('firebase_id_token')
    if (token && !stateRef.current.isLoading) {
      loadProgress()
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value: OnboardingContextType = useMemo(() => ({
    ...state,
    setOnboardingMode,
    updateBasicInfo,
    updateDetailedInterview,
    updateReviewsConfig,
    saveBasicSetup,
    generatePreview,
    saveDetailedInterviewBlock,
    uploadPhoto,
    generateAssets,
    saveAssets,
    loadLandingPageDraft,
    saveLandingPageDraft,
    validateConversionReadiness,
    sendConversionReadinessTest,
    loadProgress,
    loadReviewsConfig,
    getAuthToken,
    resetState
  }), [
    state,
    setOnboardingMode,
    updateBasicInfo,
    updateDetailedInterview,
    updateReviewsConfig,
    saveBasicSetup,
    generatePreview,
    saveDetailedInterviewBlock,
    uploadPhoto,
    generateAssets,
    saveAssets,
    loadLandingPageDraft,
    saveLandingPageDraft,
    validateConversionReadiness,
    sendConversionReadinessTest,
    loadProgress,
    loadReviewsConfig,
    getAuthToken,
    resetState
  ])

  // Ensure value is always defined (safety check)
  if (!value) {
    throw new Error('OnboardingContext value is undefined - this should never happen')
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  )
}

export const useOnboarding = () => {
  const context = useContext(OnboardingContext)
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider')
  }
  return context
}
